import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { CometChatUIKitLoginListener } from "@cometchat/chat-uikit-react";
import { Selection, useMessages, useGroupMembers } from "./useChatData";
import {
  shortTime,
  dayLabel,
  dayKey,
  roleLabel,
  splitMentions,
  mentionedNames,
  mentionsUid,
  formatFileSize,
} from "./format";
import {
  PhoneIcon,
  VideoIcon,
  InfoIcon,
  PaperclipIcon,
  SendIcon,
  ReplyIcon,
  ForwardIcon,
  FileIcon,
  DownloadIcon,
  BookmarkIcon,
  ClockIcon,
  EditIcon,
  TrashIcon,
  NoteIcon,
} from "./Icons";
import NotesPanel from "./NotesPanel";
import { conversationIdFor } from "./notes";
import { downloadAttachment } from "./download";
import ConfirmModal from "./ConfirmModal";
import { useCall } from "./CallManager";
import ForwardModal from "./ForwardModal";
import {
  addLaterItem,
  addScheduledSend,
  formatWhen,
  isMessageSaved,
  removeSavedMessage,
  subscribeLater,
  whenOptions,
} from "./later";
import AvatarHoverCard from "./AvatarHoverCard";
import Avatar from "./Avatar";
import { seedAvatarFromUser } from "./avatars";
import { useUserName } from "./names";
import { STATUS_PRESETS, readStatus } from "./status";
import type { UserStatus } from "./status";
import { purgeGroupReferences } from "./groupCleanup";
import PopoverAnchor, { useClampToViewport, clampToViewport } from "./Popover";
import ConversationFiles from "./ConversationFiles";
import MembersPanel from "./MembersPanel";
import { DmSeenIndicator, GroupSeenIndicator } from "./Receipts";
import type { DmReceipt } from "./Receipts";
import {
  filterMentionCandidates,
  findMentionQuery,
  resolveMentionedUsers,
} from "./mentions";
import type { MentionQuery } from "./mentions";
import RichComposer from "./RichComposer";
import type { RichComposerHandle } from "./RichComposer";
import * as sounds from "./sounds";

type Props = { selection: Selection | null; onCleared?: () => void; onOpenProfile?: (uid: string) => void };

const QUICK_REACTIONS = ["👍", "🔥", "dbm-m"] as const;

/** True on touch-first devices (phones/tablets), where :hover never
 * fires and hover-revealed UI would otherwise be unreachable. Checked at
 * call time rather than cached, so a hybrid laptop that switches between
 * trackpad and touchscreen answers for how it's being used right now. */
function isCoarsePointer(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.("(hover: none)").matches === true;
}

/** Renders the header, a custom flat-row message list, and the composer. */
export default function MessagePane({ selection, onCleared, onOpenProfile }: Props) {
  const { messages, loading, addLocal, updateLocal, typingFrom, deliveredUpTo, readUpTo, groupReadPulse } =
    useMessages(selection);
  const [draft, setDraft] = useState("");
  // The same content, serialized to the `**bold**` / `*italic*` wire
  // format — kept alongside the plain `draft` (used for mention detection
  // and the empty-message check) so send/schedule never need to re-derive
  // it from the composer's DOM.
  const [draftMarkdown, setDraftMarkdown] = useState("");
  const [sending, setSending] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const meUid = CometChatUIKitLoginListener.getLoggedInUser()?.getUid();
  const {
    members,
    myScope,
    owner,
    refetch: refetchMembers,
    patchScope: patchMemberScope,
    removeMemberLocal,
  } = useGroupMembers(selection?.kind === "group" ? selection.id : null);
  const { startCall } = useCall();
  const [showDetails, setShowDetails] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [replyTo, setReplyTo] = useState<CometChat.BaseMessage | null>(null);
  const [forwarding, setForwarding] = useState<CometChat.BaseMessage | null>(null);
  const [composerPop, setComposerPop] = useState<"emoji" | "schedule" | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const noteTimer = useRef<number | null>(null);
  const composerRef = useRef<RichComposerHandle>(null);
  const typingActiveRef = useRef(false);
  const typingStopTimer = useRef<number | null>(null);
  // True until the first message is sent in this conversation session —
  // see the reset effect below for why this exists.
  const sentSoundArmedRef = useRef(true);
  // A file chosen but not yet sent — held here so a caption can be typed
  // and sent WITH it, instead of firing the moment it's picked.
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  // In-progress "@…" token, and which row of the picker is highlighted.
  const [mention, setMention] = useState<MentionQuery | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  // Which "type in this style" mode is on, if any — see toggleMarker.
  const [activeMarker, setActiveMarker] = useState<"bold" | "italic" | null>(null);

  // Drop any in-progress reply target, staged file, and mention menu when
  // switching conversations — none of them make sense in the next one.
  useEffect(() => {
    setReplyTo(null);
    setPendingFile(null);
    setMention(null);
    setActiveMarker(null);
    composerRef.current?.endActiveMarker();
    // Re-arm the sent-sound for this conversation — it plays once for the
    // first message after arriving here (switching in, or opening the app
    // fresh) and then stays quiet for the rest of an active back-and-forth,
    // instead of dinging on every single message you send.
    sentSoundArmedRef.current = true;
  }, [selection]);

  // DM header: live presence + the other person's custom status.
  // `status` is the manually-set one (Online/Away/Offline/OOO + reason)
  // from the profile panel; `online` is CometChat's own socket presence.
  // Both are shown the same way the hover card already does it.
  const [peer, setPeer] = useState<{ status?: UserStatus | null; online?: boolean } | null>(null);
  // The peer as a full User object — the only mention candidate in a DM,
  // and needed as an object (not a name) for setMentionedUsers.
  const [peerUser, setPeerUser] = useState<CometChat.User | null>(null);
  useEffect(() => {
    if (!selection || selection.kind !== "user") {
      setPeer(null);
      setPeerUser(null);
      return;
    }
    let cancelled = false;
    const fetchPeer = () => {
      CometChat.getUser(selection.id)
        .then((u) => {
          if (cancelled) return;
          // Was reading `meta.statusText` — a key nothing in the app has
          // ever written, so the header silently showed no custom status
          // at all. ProfilePanel saves it under `dbmStatus`, which is
          // what readStatus() reads (same as AvatarHoverCard).
          setPeer({ status: readStatus(u), online: u.getStatus?.() === "online" });
          setPeerUser(u);
        })
        .catch(() => {});
    };
    fetchPeer();
    const presenceId = `dbm-peer-presence-${selection.id}`;
    CometChat.addUserListener(
      presenceId,
      new CometChat.UserListener({
        onUserOnline: (u: CometChat.User) => {
          if (u.getUid() === selection.id) setPeer((p) => ({ ...p, online: true }));
        },
        onUserOffline: (u: CometChat.User) => {
          if (u.getUid() === selection.id) setPeer((p) => ({ ...p, online: false }));
        },
      })
    );
    return () => {
      cancelled = true;
      CometChat.removeUserListener(presenceId);
    };
  }, [selection]);

  // Keep pinned to the latest message.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, selection]);

  // Jumping back to a specific message (from the Later panel). Runs after
  // the pin-to-bottom effect above so it wins, and only once messages have
  // actually rendered. A message older than the loaded page simply isn't
  // in the DOM — in that case this no-ops and you land at the bottom.
  const focusMessageId = selection?.focusMessageId;
  useEffect(() => {
    if (!focusMessageId) return;
    const el = scrollRef.current?.querySelector<HTMLElement>(`[data-mid="${focusMessageId}"]`);
    if (!el) return;
    el.scrollIntoView({ block: "center" });
    el.classList.add("msg--focus");
    const timer = window.setTimeout(() => el.classList.remove("msg--focus"), 2000);
    return () => window.clearTimeout(timer);
  }, [focusMessageId, messages]);

  // End any in-flight typing signal when switching conversations.
  useEffect(() => {
    if (!selection) return;
    const id = selection.id;
    const rt = selection.kind === "group" ? "group" : "user";
    return () => {
      if (typingActiveRef.current) {
        try {
          CometChat.endTyping(new CometChat.TypingIndicator(id, rt));
        } catch {
          /* ignore */
        }
        typingActiveRef.current = false;
      }
      if (typingStopTimer.current) window.clearTimeout(typingStopTimer.current);
    };
  }, [selection]);

  const groups = useMemo(() => groupByDay(messages), [messages]);

  /* Who "@" can address here: everyone in the channel, or (in a DM) just
     the person you're talking to — same rule Slack/Teams use. Yourself is
     excluded; mentioning yourself does nothing useful. */
  const mentionCandidates = useMemo<CometChat.User[]>(() => {
    if (!selection) return [];
    const list: CometChat.User[] =
      selection.kind === "group" ? (members as unknown as CometChat.User[]) : peerUser ? [peerUser] : [];
    return list.filter((u) => u.getUid() !== meUid);
  }, [selection, members, peerUser, meUid]);

  const mentionMatches = useMemo(
    () => (mention ? filterMentionCandidates(mentionCandidates, mention.query) : []),
    [mention, mentionCandidates]
  );


  if (!selection) {
    return (
      <div className="main">
        <div className="pane-empty">
          <div className="big">Select a conversation</div>
          <div>Pick a channel, person, or partner on the left</div>
        </div>
      </div>
    );
  }

  const stopTyping = () => {
    if (typingStopTimer.current) window.clearTimeout(typingStopTimer.current);
    if (!typingActiveRef.current) return;
    typingActiveRef.current = false;
    try {
      CometChat.endTyping(
        new CometChat.TypingIndicator(selection.id, selection.kind === "group" ? "group" : "user")
      );
    } catch {
      /* ignore */
    }
  };

  const pokeTyping = () => {
    if (!typingActiveRef.current) {
      typingActiveRef.current = true;
      try {
        CometChat.startTyping(
          new CometChat.TypingIndicator(selection.id, selection.kind === "group" ? "group" : "user")
        );
      } catch {
        /* ignore */
      }
    }
    if (typingStopTimer.current) window.clearTimeout(typingStopTimer.current);
    typingStopTimer.current = window.setTimeout(stopTyping, 2500);
  };

  const flashNote = (msg: string) => {
    setNote(msg);
    if (noteTimer.current) window.clearTimeout(noteTimer.current);
    noteTimer.current = window.setTimeout(() => setNote(null), 2500);
  };

  const snippetOf = (m: CometChat.BaseMessage): string => {
    if (m.getType() === "text") {
      const t = (m as CometChat.TextMessage).getText() || "";
      return t.length > 100 ? t.slice(0, 97) + "…" : t;
    }
    return "Attachment";
  };

  const conversationRef = () => ({
    kind: selection.kind,
    id: selection.id,
    name: selection.name,
  });

  /** Quote-reply: sets the banner above the composer and focuses it —
   * the reply goes out as a normal new message carrying a quoted preview
   * of the original in its metadata (see send / MessageRow). */
  const startReply = (m: CometChat.BaseMessage) => {
    setReplyTo(m);
    composerRef.current?.focus();
  };

  /** Toggle, not a one-way action — the bookmark on the message shows the
   * current state, so clicking a filled one takes it back off the list. */
  const toggleSaveForLater = (m: CometChat.BaseMessage) => {
    const id = m.getId();
    if (isMessageSaved(id)) {
      removeSavedMessage(id);
      return;
    }
    addLaterItem({
      kind: "saved",
      text: snippetOf(m),
      from: m.getSender()?.getName() || "Unknown",
      conversation: conversationRef(),
      messageId: id,
    });
  };

  const remindMe = (m: CometChat.BaseMessage, at: number) => {
    addLaterItem({
      kind: "reminder",
      text: snippetOf(m),
      from: m.getSender()?.getName() || "Unknown",
      conversation: conversationRef(),
      remindAt: at,
      notified: false,
    });
    flashNote(`Reminder set for ${formatWhen(at)} ✓`);
  };

  const scheduleSend = (at: number) => {
    const text = draftMarkdown.trim();
    if (!text) {
      flashNote("Type the message first, then pick a time");
      return;
    }
    addScheduledSend({ text, target: conversationRef(), sendAt: at });
    setDraft("");
    setDraftMarkdown("");
    composerRef.current?.clear();
    stopTyping();
    flashNote(`Scheduled for ${formatWhen(at)} ✓`);
  };

  const receiptFor = (m: CometChat.BaseMessage): DmReceipt | null => {
    // Groups get per-member receipts via GroupSeenIndicator instead —
    // there's no single sent/delivered/read state across many recipients.
    if (selection.kind === "group") return null;
    if (m.getSender()?.getUid() !== meUid) return null;
    const id = Number(m.getId());
    if (((m as any).getReadAt?.() ?? 0) > 0 || id <= readUpTo) return "read";
    if (((m as any).getDeliveredAt?.() ?? 0) > 0 || id <= deliveredUpTo) return "delivered";
    return "sent";
  };

  /** Plays the sent-sound only for the first message of this conversation
   * session, then goes quiet — an active back-and-forth doesn't need a
   * ding on every single message you send. */
  const playSentSoundOnce = () => {
    if (!sentSoundArmedRef.current) return;
    sentSoundArmedRef.current = false;
    sounds.playOnce(sounds.SOUND.messageSent);
  };

  const replyMetadata = (quoted: CometChat.BaseMessage) => ({
    dbmReply: {
      id: quoted.getId(),
      name: quoted.getSender()?.getName() || "Unknown",
      text: snippetOf(quoted),
    },
  });

  /** One send path for both plain text and "file + caption". A staged file
   * takes the draft along as the media message's caption, so the two go out
   * as a single message (Teams-style) rather than two separate ones. */
  const send = async () => {
    // draftMarkdown already reflects a still-open bold/italic run as if it
    // were closed at the current end of the text (see RichComposer), so
    // there's no separate "close the open marker" step needed here.
    const text = draftMarkdown.trim();
    const file = pendingFile;
    if ((!text && !file) || sending || attaching) return;

    const quoted = replyTo;
    setActiveMarker(null);
    setDraft("");
    setDraftMarkdown("");
    composerRef.current?.clear();
    setPendingFile(null);
    setReplyTo(null);
    setMention(null);
    stopTyping();

    const receiverType = selection.kind === "group" ? "group" : "user";
    const restore = () => {
      composerRef.current?.setContent(text);
      setPendingFile(file);
      if (quoted) setReplyTo(quoted);
    };

    if (file) {
      setAttaching(true);
      try {
        const type = file.type.startsWith("image/")
          ? CometChat.MESSAGE_TYPE.IMAGE
          : file.type.startsWith("video/")
          ? CometChat.MESSAGE_TYPE.VIDEO
          : file.type.startsWith("audio/")
          ? CometChat.MESSAGE_TYPE.AUDIO
          : CometChat.MESSAGE_TYPE.FILE;
        const msg = new CometChat.MediaMessage(selection.id, file, type, receiverType);
        if (text) msg.setCaption(text);
        if (quoted) msg.setMetadata(replyMetadata(quoted));
        const sent = (await CometChat.sendMediaMessage(msg)) as CometChat.BaseMessage;
        addLocal(sent);
        playSentSoundOnce();
      } catch (e) {
        console.warn("file send failed", e);
        restore();
      } finally {
        setAttaching(false);
      }
      return;
    }

    setSending(true);
    try {
      const msg = new CometChat.TextMessage(selection.id, text, receiverType);
      // Registering the mentions server-side is what makes them real:
      // it survives a reload and drives hasMentionedMe for the recipient,
      // rather than relying on the text merely looking like "@Someone".
      const mentioned = resolveMentionedUsers(text, mentionCandidates);
      if (mentioned.length) msg.setMentionedUsers(mentioned);
      if (quoted) msg.setMetadata(replyMetadata(quoted));
      const sent = (await CometChat.sendMessage(msg)) as CometChat.BaseMessage;
      addLocal(sent);
      playSentSoundOnce();
    } catch (e) {
      console.warn("send failed", e);
      restore(); // restore so the user doesn't lose it
    } finally {
      setSending(false);
    }
  };

  const isChannel = selection.kind === "group";
  const isAnnouncements = isChannel && selection.id === "announcements";

  /** Fires on every keystroke inside the composer — mirrors its plain
   * text/markdown back into state and re-evaluates whether an "@…" picker
   * should be open. */
  const handleComposerChange = (plain: string, markdown: string, caret: number) => {
    setDraft(plain);
    setDraftMarkdown(markdown);
    const found = findMentionQuery(plain, caret);
    setMention(found);
    if (found) setMentionIndex(0);
  };

  /** Swaps the half-typed "@Jan" for the picked "@Jane Doe " and closes. */
  const pickMention = (user: CometChat.User) => {
    if (!mention) return;
    const cursor = composerRef.current?.getCaret() ?? draft.length;
    const insert = `@${user.getName()} `;
    composerRef.current?.replaceRange(mention.start, cursor, insert, mention.start + insert.length);
    setMention(null);
  };

  /** The "@" toolbar button — inserts the trigger and opens the picker. */
  const openMentionPicker = () => {
    const cursor = composerRef.current?.getCaret() ?? draft.length;
    // findMentionQuery anchors on start-of-string or whitespace, so an "@"
    // jammed against the previous word would never open the picker.
    const needsSpace = cursor > 0 && !/\s$/.test(draft.slice(0, cursor));
    const insert = `${needsSpace ? " " : ""}@`;
    composerRef.current?.replaceRange(cursor, cursor, insert, cursor + insert.length);
  };

  /** Inserts an emoji at the cursor (or right before a selection, keeping
   * it intact) — plain insertion, no styling involved. */
  const insertEmoji = (emoji: string) => {
    const start = composerRef.current?.getCaret() ?? draft.length;
    composerRef.current?.replaceRange(start, start, emoji, start + emoji.length);
  };

  /**
   * Bold/Italic as a real toggle, now backed by RichComposer: with a
   * selection, it wraps the selection immediately; with none, it's a
   * "type in this style" mode — clicking Bold starts a run that
   * everything you type actually renders bold INSIDE, live, until you
   * click Bold again (closing it) or Italic (closing bold, opening
   * italic instead — the two are mutually exclusive). See RichComposer
   * for how that live rendering works; this just delegates to it and
   * mirrors the resulting mode for the toolbar button's active state.
   */
  const toggleMarker = (kind: "bold" | "italic") => {
    composerRef.current?.toggleMarker(kind);
  };

  return (
    <div className="main">
      <div className="ch-head">
        {/* Same avatar + hover card as the sidebar rows — the header was
            the one place a person appeared with no picture to recognise
            them by, and nothing to hover for their details. */}
        {selection.kind === "user" && (
          <AvatarHoverCard
            uid={selection.id}
            name={selection.name}
            onOpenProfile={onOpenProfile}
          >
            <Avatar uid={selection.id} name={selection.name} size="av" />
          </AvatarHoverCard>
        )}
        <div className="ttl">
          <b>
            {isChannel && <span className="hash">#</span>}
            {selection.name}
          </b>
          {selection.kind === "user" && peer ? (
            <span>
              {/* A manually-set status wins over raw socket presence —
                  "Out of office" shouldn't be overridden by the fact that
                  a tab happens to be open. The dot still reflects the
                  real connection. Offline is labelled "Offline", not
                  "Away": CometChat presence is only ever online/offline,
                  so calling it "Away" told people someone had stepped out
                  when all it meant was "chat isn't open right now". */}
              <i
                className={`presence-dot ${peer.online ? "onl" : ""}`}
                style={peer.status ? { background: STATUS_PRESETS[peer.status.preset].color } : undefined}
              />
              {peer.status ? STATUS_PRESETS[peer.status.preset].label : peer.online ? "Online" : "Offline"}
              {peer.status?.reason ? ` · ${peer.status.reason}` : ""}
            </span>
          ) : (
            selection.subtitle && <span>{selection.subtitle}</span>
          )}
        </div>
        <div className="members">
          {/* Facepile is the way into the participants panel — same
              affordance Teams uses, rather than burying members inside
              the Details sheet. */}
          {isChannel && members.length > 0 && (
            <button
              className="facepile"
              title="View participants"
              aria-label="View participants"
              onClick={() => {
                sounds.playOnce(sounds.SOUND.click);
                setShowMembers(true);
              }}
            >
              {members.slice(0, 3).map((m) => (
                <Avatar key={m.getUid()} uid={m.getUid()} name={m.getName()} />
              ))}
              {selection.membersCount != null && selection.membersCount > 3 && (
                <span className="av" style={{ fontSize: 9 }}>+{selection.membersCount - 3}</span>
              )}
              {selection.membersCount != null && (
                <span className="membercount">{selection.membersCount}</span>
              )}
            </button>
          )}
          {!(selection.kind === "group" && selection.isStructuredChannel) && (
            <>
              <button
                className="hbtn hbtn--call"
                title="Audio call"
                aria-label="Audio call"
                onClick={() => {
                  sounds.playOnce(sounds.SOUND.click);
                  startCall(selection.id, isChannel ? "group" : "user", "audio", selection.name);
                }}
              >
                <PhoneIcon size={17} />
              </button>
              <button
                className="hbtn hbtn--call"
                title="Video call"
                aria-label="Video call"
                onClick={() => {
                  sounds.playOnce(sounds.SOUND.click);
                  startCall(selection.id, isChannel ? "group" : "user", "video", selection.name);
                }}
              >
                <VideoIcon size={17} />
              </button>
            </>
          )}
          <button
            className={`hbtn ${showNotes ? "active" : ""}`}
            title="Notes"
            aria-label="Notes"
            onClick={() => {
              sounds.playOnce(sounds.SOUND.click);
              setShowNotes((v) => !v);
            }}
          >
            <NoteIcon size={17} />
          </button>
          <button
            className={`hbtn ${showDetails ? "active" : ""}`}
            title="Details"
            aria-label="Details"
            onClick={() => {
              sounds.playOnce(sounds.SOUND.click);
              setShowDetails((v) => !v);
            }}
          >
            <InfoIcon size={17} />
          </button>
        </div>
      </div>

      {showNotes && (
        <NotesPanel
          conversationId={conversationIdFor(selection)}
          title={selection.name}
          onClose={() => setShowNotes(false)}
        />
      )}

      {showDetails && (
        <DetailsPanel
          selection={selection}
          members={members}
          onViewMembers={() => {
            setShowDetails(false);
            setShowMembers(true);
          }}
          onClose={() => setShowDetails(false)}
        />
      )}

      {showMembers && isChannel && (
        <MembersPanel
          selection={selection}
          members={members}
          myScope={myScope}
          owner={owner}
          meUid={meUid}
          onMembersChanged={refetchMembers}
          onScopeChanged={patchMemberScope}
          onMemberRemoved={removeMemberLocal}
          onLeft={() => {
            setShowMembers(false);
            purgeGroupReferences(selection.id);
            onCleared?.();
          }}
          onClose={() => setShowMembers(false)}
        />
      )}

      {forwarding && (
        <ForwardModal message={forwarding} onClose={() => setForwarding(null)} onDone={() => {}} />
      )}

      <div className="msgs" ref={scrollRef}>
        {loading && messages.length === 0 && (
          <div className="pane-empty" style={{ minHeight: 200 }}>
            <div>Loading messages…</div>
          </div>
        )}
        {!loading && messages.length === 0 && (
          <div className="pane-empty" style={{ minHeight: 200 }}>
            <div className="big">No messages yet</div>
            <div>Say something to start the conversation</div>
          </div>
        )}

        {groups.map((group) => (
          <div key={group.key}>
            <div className="divider">
              <span>{group.label}</span>
            </div>
            {group.messages.map((m, i) => {
              const isCall = m.getCategory() === CometChat.CATEGORY_CALL;
              const rowType = isCall ? "call" : isAnnouncements ? "announce" : "message";
              const prev = i > 0 ? group.messages[i - 1] : null;
              const prevType = prev
                ? prev.getCategory() === CometChat.CATEGORY_CALL
                  ? "call"
                  : isAnnouncements
                  ? "announce"
                  : "message"
                : null;
              const grouped =
                rowType !== "announce" &&
                prevType === rowType &&
                prev!.getSender()?.getUid() === m.getSender()?.getUid() &&
                m.getSentAt() - prev!.getSentAt() <= 300;

              return isCall ? (
                <CallLogRow
                  key={m.getId()}
                  message={m as CometChat.Call}
                  mine={m.getSender()?.getUid() === meUid}
                  grouped={grouped}
                  onOpenProfile={onOpenProfile}
                />
              ) : isAnnouncements ? (
                <AnnouncementCard key={m.getId()} message={m} />
              ) : (
                <MessageRow
                  key={m.getId()}
                  message={m}
                  mine={m.getSender()?.getUid() === meUid}
                  meUid={meUid}
                  grouped={grouped}
                  onUpdate={updateLocal}
                  receipt={receiptFor(m)}
                  inGroup={isChannel}
                  groupGuid={isChannel ? selection.id : undefined}
                  groupReadPulse={groupReadPulse}
                  onReply={startReply}
                  onForward={setForwarding}
                  onSave={toggleSaveForLater}
                  onRemind={remindMe}
                  onOpenProfile={onOpenProfile}
                />
              );
            })}
          </div>
        ))}
      </div>

      {/* A permanent reserved strip between the messages and the composer,
         not inside either one — always mounted, even with nothing to
         show, so the gap itself never changes size. That's what actually
         fixes the overlap: previously this floated with negative
         positioning and no reserved space of its own, so a scrolled
         message could slide right up underneath it. Deliberately its own
         row (not part of .composer's box) so the input's border doesn't
         grow, and so this slot is free to carry other things later
         (an out-of-office banner, an alert) without touching the input. */}
      <div className={`typingline ${note ? "typingline--note" : ""}`}>
        {note ? (
          note
        ) : (
          typingFrom && (
            <>
              {typingFrom} is typing<span className="typingline__ellip" />
            </>
          )
        )}
      </div>

      <div className="composer">
        {replyTo && (
          <div className="replybar">
            <div className="mquote">
              <b>Replying to {replyTo.getSender()?.getName() || "Unknown"}</b>
              <span>{snippetOf(replyTo)}</span>
            </div>
            <button className="replybar__close" title="Cancel reply" aria-label="Cancel reply" onClick={() => setReplyTo(null)}>
              ✕
            </button>
          </div>
        )}
        {pendingFile && (
          <div className="attachbar">
            <AttachmentPreview file={pendingFile} />
            <button
              className="replybar__close"
              title="Remove attachment"
              aria-label="Remove attachment"
              disabled={attaching}
              onClick={() => setPendingFile(null)}
            >
              ✕
            </button>
          </div>
        )}
        <div className="composer-input">
          {mention && mentionMatches.length > 0 && (
            <MentionMenu
              matches={mentionMatches}
              activeIndex={mentionIndex}
              onHover={setMentionIndex}
              onPick={pickMention}
            />
          )}
          <RichComposer
            ref={composerRef}
            placeholder={
              pendingFile
                ? "Add a caption…"
                : `Message ${isChannel ? "#" + selection.name : selection.name}`
            }
            onChange={(plain, markdown, caret) => {
              handleComposerChange(plain, markdown, caret);
              pokeTyping();
            }}
            onActiveMarkerChange={setActiveMarker}
            onEnter={() => void send()}
            onPasteImage={setPendingFile}
            onKeyDownBefore={(e) => {
              // The picker owns these keys while it's open.
              if (mention && mentionMatches.length > 0) {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setMentionIndex((i) => (i + 1) % mentionMatches.length);
                  return;
                }
                if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setMentionIndex((i) => (i - 1 + mentionMatches.length) % mentionMatches.length);
                  return;
                }
                if (e.key === "Enter" || e.key === "Tab") {
                  e.preventDefault();
                  // Clamped: the match list can shrink underneath the
                  // highlight if group members finish loading late.
                  pickMention(mentionMatches[Math.min(mentionIndex, mentionMatches.length - 1)]);
                  return;
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  setMention(null);
                  return;
                }
              }
            }}
          />
        </div>
        <div className="comp-row">
          <input
            ref={fileInputRef}
            type="file"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = ""; // allow re-selecting the same file next time
              if (file) {
                setPendingFile(file);
                composerRef.current?.focus();
              }
            }}
          />
          <button
            title={pendingFile ? "Replace attachment" : "Attach a file"}
            aria-label="Attach"
            disabled={attaching}
            onClick={() => fileInputRef.current?.click()}
          >
            {attaching ? <span className="spin" /> : <PaperclipIcon size={15} />}
          </button>
          <button
            className={activeMarker === "bold" ? "on" : ""}
            title="Bold"
            aria-label="Bold"
            onClick={() => toggleMarker("bold")}
          >
            <b>B</b>
          </button>
          <button
            className={activeMarker === "italic" ? "on" : ""}
            title="Italic"
            aria-label="Italic"
            onClick={() => toggleMarker("italic")}
          >
            <i>I</i>
          </button>
          <button title="Mention someone" aria-label="Mention someone" onClick={openMentionPicker}>@</button>
          <PopoverAnchor active={composerPop === "emoji"} onDismiss={() => setComposerPop(null)}>
            <button
              title="Emoji"
              aria-label="Emoji"
              onClick={() => setComposerPop((v) => (v === "emoji" ? null : "emoji"))}
            >🙂</button>
            {composerPop === "emoji" && (
              <EmojiPicker
                onPick={(e) => insertEmoji(e)}
                onClose={() => setComposerPop(null)}
              />
            )}
          </PopoverAnchor>
          <PopoverAnchor active={composerPop === "schedule"} onDismiss={() => setComposerPop(null)}>
            <button
              title="Send later"
              aria-label="Send later"
              onClick={() => setComposerPop((v) => (v === "schedule" ? null : "schedule"))}
            >⏱︎</button>
            {composerPop === "schedule" && (
              <WhenMenu
                onPick={(at) => scheduleSend(at)}
                onClose={() => setComposerPop(null)}
              />
            )}
          </PopoverAnchor>
          <button
            className="send"
            title="Send"
            aria-label="Send"
            disabled={sending || attaching || (!draft.trim() && !pendingFile)}
            onClick={() => void send()}
          >
            <SendIcon size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

/** Renders **bold** and *italic* markers as real <b>/<i> — the composer's
 * Bold/Italic buttons insert this syntax, so it needs to render back out or
 * the sender's own asterisks show up literally to everyone reading it. */
function renderFormatted(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /\*\*(.+?)\*\*|\*(.+?)\*/g;
  let last = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(m[1] !== undefined ? <b key={key++}>{m[1]}</b> : <i key={key++}>{m[2]}</i>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function MessageText({ text, names }: { text: string; names?: string[] }) {
  return (
    <p className="mtext">
      {splitMentions(text, names).map((part, i) =>
        part.mention ? (
          <span className="mention" key={i}>{part.text}</span>
        ) : (
          <span key={i}>{renderFormatted(part.text)}</span>
        )
      )}
    </p>
  );
}

/** Renders an image/video/audio/file attachment inline in the message row,
 * with the sender's caption underneath it when there is one. */
function MediaContent({ message, names }: { message: CometChat.MediaMessage; names?: string[] }) {
  const attachment = message.getAttachment();
  const url = attachment?.getUrl();
  const name = attachment?.getName() || "Attachment";
  const size = formatFileSize(attachment?.getSize());
  const type = message.getType();
  const caption = message.getCaption?.() || "";

  if (!url) return <p className="mtext">Attachment unavailable</p>;

  const media =
    type === CometChat.MESSAGE_TYPE.IMAGE ? (
      <a className="mmedia-img" href={url} target="_blank" rel="noreferrer">
        <img src={url} alt={name} loading="lazy" />
      </a>
    ) : type === CometChat.MESSAGE_TYPE.VIDEO ? (
      <video className="mmedia-video" src={url} controls preload="metadata" />
    ) : type === CometChat.MESSAGE_TYPE.AUDIO ? (
      <audio className="mmedia-audio" src={url} controls />
    ) : (
      <button className="mmedia-file" onClick={() => void downloadAttachment(url, name)}>
        <span className="mmedia-file-icon">
          <DownloadIcon size={20} />
        </span>
        <span className="mmedia-file-info">
          <span className="nm">{name}</span>
          {size && <span className="sz">{size}</span>}
        </span>
      </button>
    );

  if (!caption) return media;
  return (
    <>
      {media}
      <MessageText text={caption} names={names} />
    </>
  );
}

/** The staged-but-not-yet-sent attachment shown above the composer. */
function AttachmentPreview({ file }: { file: File }) {
  const [url, setUrl] = useState<string | null>(null);

  // Object URLs leak until revoked, so tie the lifetime to this file.
  useEffect(() => {
    if (!file.type.startsWith("image/")) {
      setUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
      setUrl(null);
    };
  }, [file]);

  return (
    <div className="attachbar__item">
      {url ? (
        <img className="attachbar__thumb" src={url} alt="" />
      ) : (
        <span className="attachbar__icon">
          <FileIcon size={16} />
        </span>
      )}
      <span className="attachbar__info">
        <span className="nm">{file.name}</span>
        <span className="sz">{formatFileSize(file.size)}</span>
      </span>
    </div>
  );
}

/** Typeahead list of people an "@" can address, above the composer. */
function MentionMenu({
  matches,
  activeIndex,
  onHover,
  onPick,
}: {
  matches: CometChat.User[];
  activeIndex: number;
  onHover: (i: number) => void;
  onPick: (u: CometChat.User) => void;
}) {
  return (
    <div className="mention-pop" role="listbox" aria-label="Mention someone">
      {matches.map((u, i) => (
        <button
          key={u.getUid()}
          className={`mention-pop__row ${i === activeIndex ? "on" : ""}`}
          role="option"
          aria-selected={i === activeIndex}
          onMouseEnter={() => onHover(i)}
          // Keeps focus in the composer so the caret position stays valid.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onPick(u)}
        >
          <Avatar uid={u.getUid()} name={u.getName()} size="dav" />
          <span className="nm">{u.getName()}</span>
        </button>
      ))}
    </div>
  );
}

function MessageRow({
  message,
  mine,
  meUid,
  grouped,
  onUpdate,
  receipt,
  inGroup,
  groupGuid,
  groupReadPulse,
  onReply,
  onForward,
  onSave,
  onRemind,
  onOpenProfile,
}: {
  message: CometChat.BaseMessage;
  mine: boolean;
  meUid?: string;
  grouped?: boolean;
  onUpdate: (msg: CometChat.BaseMessage) => void;
  receipt?: DmReceipt | null;
  inGroup?: boolean;
  groupGuid?: string;
  groupReadPulse?: number;
  onReply?: (msg: CometChat.BaseMessage) => void;
  onForward?: (msg: CometChat.BaseMessage) => void;
  onSave?: (msg: CometChat.BaseMessage) => void;
  onRemind?: (msg: CometChat.BaseMessage, at: number) => void;
  onOpenProfile?: (uid: string) => void;
}) {
  const msgToolsRef = useRef<HTMLDivElement>(null);

  // Subscribed rather than read once, so the bookmark also un-fills when
  // the same message is removed from the Later panel instead of here.
  const messageId = message.getId();
  // Touch-only: stands in for hover to reveal this row's action toolbar.
  const [toolsOpen, setToolsOpen] = useState(false);
  const [saved, setSaved] = useState(() => isMessageSaved(messageId));
  useEffect(() => {
    setSaved(isMessageSaved(messageId));
    return subscribeLater(() => setSaved(isMessageSaved(messageId)));
  }, [messageId]);

  const sender = message.getSender();
  seedAvatarFromUser(sender); // senders carry their avatar — no lookup needed
  // Deliberately NOT seeding the name cache from `sender` here — a
  // message's embedded sender is exactly the stale snapshot useUserName
  // exists to route around. Seeding from it would overwrite a correctly
  // live-fetched name right back to whatever this old message froze in.
  const senderUid = sender?.getUid() || "";
  const name = useUserName(senderUid, sender?.getName() || "Unknown");
  const role = roleLabel(sender?.getMetadata?.());
  const isMedia = [
    CometChat.MESSAGE_TYPE.IMAGE,
    CometChat.MESSAGE_TYPE.VIDEO,
    CometChat.MESSAGE_TYPE.AUDIO,
    CometChat.MESSAGE_TYPE.FILE,
  ].includes(message.getType());
  const isText = !isMedia && message.getType() === "text";
  const text = isText ? (message as CometChat.TextMessage).getText() : "";
  const isDeleted = !!message.getDeletedAt();
  const isEdited = !!message.getEditedAt();
  const names = mentionedNames(message as any);
  const mentionsMe = !mine && mentionsUid(message as any, meUid);

  const [reactions, setReactions] = useState(() => toReactionMap(message));
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(text);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const saveEdit = async () => {
    const next = editText.trim();
    if (!next || next === text) {
      setEditing(false);
      return;
    }
    try {
      const edited = message as CometChat.TextMessage;
      edited.setText(next);
      const updated = (await CometChat.editMessage(edited)) as CometChat.BaseMessage;
      onUpdate(updated);
      setEditing(false);
    } catch (e) {
      console.warn("edit failed", e);
    }
  };

  const deleteMessage = async () => {
    try {
      const updated = await CometChat.deleteMessage(String(message.getId()));
      onUpdate(updated);
    } catch (e) {
      console.warn("delete failed", e);
    }
  };

  const toggle = async (emoji: string) => {
    if (busy) return;
    setBusy(emoji);
    const wasMine = reactions[emoji]?.me;
    // Optimistic update.
    setReactions((prev) => {
      const cur = prev[emoji] || { count: 0, me: false };
      const nextCount = wasMine ? Math.max(0, cur.count - 1) : cur.count + 1;
      return { ...prev, [emoji]: { count: nextCount, me: !wasMine } };
    });
    try {
      if (wasMine) await CometChat.removeReaction(message.getId(), emoji);
      else await CometChat.addReaction(message.getId(), emoji);
    } catch (e) {
      console.warn("reaction failed", e);
      setReactions(toReactionMap(message)); // revert to last known good on failure
    } finally {
      setBusy(null);
    }
  };

  // One popover open at a time within this row's toolbar.
  const [pop, setPop] = useState<"emoji" | "remind" | null>(null);
  // Any emoji with reactions shows — the picker can add ones beyond the quick three.
  const allReactions = Object.keys(reactions).filter((r) => (reactions[r]?.count || 0) > 0);
  const quoted = readReplyQuote(message);

  if (isDeleted) {
    return (
      <div className={`msg ${mine ? "mine" : ""} ${grouped ? "grouped" : ""}`} data-mid={message.getId()}>
        <AvatarHoverCard uid={senderUid} name={name} onOpenProfile={onOpenProfile}>
          <Avatar uid={senderUid} name={name} colored={!mine} />
        </AvatarHoverCard>
        <div className="mbody bubble">
          <div className="mmeta">
            <b>{name}</b>
            <time>{shortTime(message.getSentAt())}</time>
          </div>
          <p className="mtext mtext-deleted">Message deleted</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`msg ${mine ? "mine" : ""} ${grouped ? "grouped" : ""} ${
        mentionsMe ? "mentions-me" : ""
      } ${toolsOpen ? "tools-open" : ""}`}
      data-mid={messageId}
      // The toolbar itself was never viewport-clamped before — only its
      // OWN nested popovers (emoji/remind) were — so it could still run
      // past the message pane's edge and under the sidebar. Runs fresh on
      // every hover rather than once on mount, since the pane's available
      // width can change between hovers (sidebar toggled, window resized).
      onMouseEnter={() => clampToViewport(msgToolsRef.current)}
      // Touch has no hover, so the reply/react/save toolbar was simply
      // unreachable on a phone. Tapping the message stands in for hovering
      // it. Gated on the pointer actually being coarse so a mouse user's
      // click never sticks the toolbar open — desktop keeps pure hover.
      onClick={(e) => {
        if (!isCoarsePointer()) return;
        // Let the toolbar's own buttons (and links/media inside the
        // bubble) act normally instead of just toggling it shut again.
        if ((e.target as HTMLElement).closest("button, a, input, textarea")) return;
        setToolsOpen((v) => {
          const next = !v;
          if (next) requestAnimationFrame(() => clampToViewport(msgToolsRef.current));
          return next;
        });
      }}
    >
      <AvatarHoverCard uid={senderUid} name={name} onOpenProfile={onOpenProfile}>
        <Avatar uid={senderUid} name={name} colored={!mine} />
      </AvatarHoverCard>
      <div className="mbody bubble">
        <div className="mmeta">
          <b>{name}</b>
          {role && <span className={`role ${role.isPartner ? "extrole" : ""}`}>{role.text}</span>}
          <time>{shortTime(message.getSentAt())}</time>
          {isEdited && <span className="edited-tag">(edited)</span>}
        </div>
        {quoted && (
          <div className="mquote">
            <b>{quoted.name}</b>
            <span>{quoted.text}</span>
          </div>
        )}
        {editing ? (
          <div className="edit-row">
            <input
              autoFocus
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void saveEdit();
                if (e.key === "Escape") setEditing(false);
              }}
            />
            <button className="modal-btn" onClick={() => void saveEdit()}>Save</button>
            <button className="edit-cancel" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        ) : isMedia ? (
          <MediaContent message={message as CometChat.MediaMessage} names={names} />
        ) : (
          <MessageText text={text} names={names} />
        )}
        {/* Reactions someone already left — real state, always visible. */}
        {allReactions.length > 0 && (
          <div className="reacts">
            {allReactions.map((emoji) => {
              const r = reactions[emoji];
              return (
                <button
                  key={emoji}
                  className={`react ${r?.me ? "me" : ""}`}
                  onClick={() => void toggle(emoji)}
                  disabled={busy === emoji}
                >
                  {emoji === "dbm-m" ? <span className="dbm-emoji">m.</span> : emoji}
                  <b>{r?.count || 0}</b>
                </button>
              );
            })}
          </div>
        )}
        {/* Read receipt sits in the bubble's bottom-right corner rather
           than up in the meta row, where the avatar hover card covered it. */}
        {mine && (receipt || inGroup) && (
          <div className="mseen">
            {inGroup ? (
              <GroupSeenIndicator messageId={message.getId()} guid={groupGuid} livePulse={groupReadPulse} />
            ) : (
              receipt && <DmSeenIndicator state={receipt} />
            )}
          </div>
        )}
        {/* Hover toolbar — anchored to the bubble itself (not the row), so
           it tracks the bubble's real position regardless of which side
           it's aligned to. Hidden until you hover the row. */}
        <div className="msg-tools" ref={msgToolsRef}>
        {QUICK_REACTIONS.map((emoji) => (
          <button
            key={emoji}
            className={`msg-tool ${reactions[emoji]?.me ? "me" : ""}`}
            title={emoji === "dbm-m" ? "React" : `React ${emoji}`}
            onClick={() => void toggle(emoji)}
            disabled={busy === emoji}
          >
            {emoji === "dbm-m" ? <span className="dbm-emoji">m.</span> : emoji}
          </button>
        ))}
        <PopoverAnchor active={pop === "emoji"} onDismiss={() => setPop(null)}>
          <button
            className="msg-tool"
            title="More reactions"
            onClick={() => setPop((v) => (v === "emoji" ? null : "emoji"))}
          >+</button>
          {pop === "emoji" && (
            <EmojiPicker
              onPick={(e) => void toggle(e)}
              onClose={() => setPop(null)}
            />
          )}
        </PopoverAnchor>
        <span className="msg-tool-sep" />
        {onReply && (
          <button className="msg-tool" title="Reply" onClick={() => onReply(message)}>
            <ReplyIcon size={17} />
          </button>
        )}
        {onForward && (
          <button className="msg-tool" title="Forward" onClick={() => onForward(message)}>
            <ForwardIcon size={17} />
          </button>
        )}
        {onSave && (
          <button
            className={`msg-tool ${saved ? "me" : ""}`}
            title={saved ? "Saved — click to remove" : "Save for later"}
            onClick={() => onSave(message)}
          >
            <BookmarkIcon size={17} active={saved} />
          </button>
        )}
        {onRemind && (
          <PopoverAnchor active={pop === "remind"} onDismiss={() => setPop(null)}>
            <button
              className="msg-tool"
              title="Remind me"
              onClick={() => setPop((v) => (v === "remind" ? null : "remind"))}
            >
              <ClockIcon size={17} />
            </button>
            {pop === "remind" && (
              <WhenMenu
                onPick={(at) => onRemind(message, at)}
                onClose={() => setPop(null)}
              />
            )}
          </PopoverAnchor>
        )}
        {mine && (isText || isMedia) && (
          <>
            <span className="msg-tool-sep" />
            {isText && (
              <button className="msg-tool" title="Edit" onClick={() => { setEditText(text); setEditing(true); }}>
                <EditIcon size={17} />
              </button>
            )}
            <button className="msg-tool msg-tool--danger" title="Delete" onClick={() => setConfirmingDelete(true)}>
              <TrashIcon size={17} />
            </button>
          </>
        )}
        {confirmingDelete && (
          <ConfirmModal
            title="Delete this message?"
            body="This can't be undone."
            confirmLabel="Delete"
            danger
            onConfirm={() => {
              setConfirmingDelete(false);
              void deleteMessage();
            }}
            onCancel={() => setConfirmingDelete(false)}
          />
        )}
        </div>
      </div>
    </div>
  );
}

/** Reads the quoted-original preview off a reply message's metadata. */
function readReplyQuote(message: CometChat.BaseMessage): { name: string; text: string } | null {
  let meta: any = (message as any).getMetadata?.() || {};
  if (typeof meta === "string") {
    try {
      meta = JSON.parse(meta);
    } catch {
      meta = {};
    }
  }
  const q = meta?.dbmReply;
  if (!q || !q.name) return null;
  return { name: q.name, text: q.text || "" };
}

/** Common-emoji grid popover — used for reactions and composer insertion. */
const EMOJIS = [
  "👍", "👎", "😀", "😂", "😅", "😍", "🤔", "😢",
  "😮", "😡", "🎉", "🔥", "❤️", "🙏", "👏", "💯",
  "✅", "❌", "⚡", "💪", "👀", "🤝", "🚀", "☕",
  "🍺", "😴", "🤯", "🥳", "😎", "🙈", "💬", "🧱",
];

/** Small vertical "when" popover shared by remind-me and send-later. */
function WhenMenu({ onPick, onClose }: { onPick: (at: number) => void; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useClampToViewport(ref);
  return (
    <div className="when-pop" ref={ref}>
      {whenOptions().map((o) => (
        <button
          key={o.label}
          onClick={() => {
            onPick(o.at);
            onClose();
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function EmojiPicker({ onPick, onClose }: { onPick: (emoji: string) => void; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useClampToViewport(ref);
  return (
    <div className="emoji-pop" ref={ref}>
      {EMOJIS.map((e) => (
        <button
          key={e}
          onClick={() => {
            onPick(e);
            onClose();
          }}
        >
          {e}
        </button>
      ))}
    </div>
  );
}

function toReactionMap(message: CometChat.BaseMessage): Record<string, { count: number; me: boolean }> {
  const out: Record<string, { count: number; me: boolean }> = {};
  const list = message.getReactions?.() || [];
  for (const r of list) {
    out[r.getReaction()] = { count: r.getCount(), me: r.getReactedByMe() };
  }
  return out;
}

/** Static-look announcement card — no live read-tracking data yet, so no fake count shown. */
function AnnouncementCard({ message }: { message: CometChat.BaseMessage }) {
  const text = message.getType() === "text" ? (message as CometChat.TextMessage).getText() : "";
  const splitAt = text.indexOf(". ");
  const title = splitAt > -1 ? text.slice(0, splitAt) : text;
  const body = splitAt > -1 ? text.slice(splitAt + 2) : "";

  return (
    <div className="announce">
      <div className="an-head">
        <span className="an-tag">Announcement</span>
        <b>{title}</b>
      </div>
      {body && <p>{body}</p>}
      <div className="an-foot">
        <button className="an-ack">✓ Mark as read</button>
        <span>{shortTime(message.getSentAt())}</span>
      </div>
    </div>
  );
}

const CALL_ACTION_LABELS: Record<string, string> = {
  initiated: "Started",
  ongoing: "In progress",
  unanswered: "No answer",
  busy: "Busy",
  rejected: "Declined",
  cancelled: "Cancelled",
  ended: "Ended",
};

/** Inline call-log row — replaces the raw "[audio]"/"[video]" fallback text. */
function CallLogRow({
  message,
  mine,
  grouped,
  onOpenProfile,
}: {
  message: CometChat.Call;
  mine: boolean;
  grouped?: boolean;
  onOpenProfile?: (uid: string) => void;
}) {
  const sender = message.getSender();
  seedAvatarFromUser(sender); // senders carry their avatar — no lookup needed
  // Deliberately NOT seeding the name cache from `sender` here — a
  // message's embedded sender is exactly the stale snapshot useUserName
  // exists to route around. Seeding from it would overwrite a correctly
  // live-fetched name right back to whatever this old message froze in.
  const senderUid = sender?.getUid() || "";
  const name = useUserName(senderUid, sender?.getName() || "Unknown");
  const isVideo = message.getType() === "video";
  const action = message.getAction();
  const status = CALL_ACTION_LABELS[action] || action;

  return (
    <div className={`msg ${mine ? "mine" : ""} ${grouped ? "grouped" : ""}`}>
      <AvatarHoverCard uid={senderUid} name={name} onOpenProfile={onOpenProfile}>
        <Avatar uid={senderUid} name={name} colored={!mine} />
      </AvatarHoverCard>
      <div className="mbody">
        <div className="mmeta">
          <b>{name}</b>
          <time>{shortTime(message.getSentAt())}</time>
        </div>
        <div className="call-log">
          <span className="call-log-icon">{isVideo ? "▣︎" : "✆︎"}</span>
          <span>{isVideo ? "Video call" : "Audio call"} · {status}</span>
        </div>
      </div>
    </div>
  );
}

/** Details side panel — conversation/channel info, member list, and channel management. */
function DetailsPanel({
  selection,
  members,
  onViewMembers,
  onClose,
}: {
  selection: Selection;
  members: CometChat.GroupMember[];
  onViewMembers: () => void;
  onClose: () => void;
}) {
  const isChannel = selection.kind === "group";

  return (
    <aside className="details-panel">
      <div className="modal-head">
        <b>Details</b>
        <button className="hbtn" onClick={onClose} aria-label="Close">✕</button>
      </div>
      <div className="modal-section">
        <div className="modal-label">
          {isChannel ? (selection.isStructuredChannel ? "Channel" : "Group") : "Person"}
        </div>
        <div className="details-name">{selection.isStructuredChannel && "#"}{selection.name}</div>
        {selection.subtitle && <div className="details-sub">{selection.subtitle}</div>}
      </div>
      {/* Participants, adding people, roles, leaving and deleting all live
          in MembersPanel now (opened from the header facepile) — this panel
          stays focused on what the conversation IS, plus its files. */}
      {isChannel && (
        <div className="modal-section">
          <button className="modal-btn" onClick={onViewMembers}>
            View participants ({members.length})
          </button>
        </div>
      )}

      <ConversationFiles selection={selection} />
    </aside>
  );
}

function groupByDay(messages: CometChat.BaseMessage[]) {
  const out: { key: string; label: string; messages: CometChat.BaseMessage[] }[] = [];
  for (const m of messages) {
    const k = dayKey(m.getSentAt());
    const last = out[out.length - 1];
    if (last && last.key === k) last.messages.push(m);
    else out.push({ key: k, label: dayLabel(m.getSentAt()), messages: [m] });
  }
  return out;
}
